import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, ExternalLink, ArrowLeft } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/Button";
import { useAuth } from "../contexts/AuthContext";
import { getUserPropertyReports, getReportPdfUrl } from "../supabase";

const Reports = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadReports = useCallback(async () => {
    if (!user?.email) return;
    try {
      setLoading(true);
      const userReports = await getUserPropertyReports(user.email);
      setReports(userReports || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.email) {
      loadReports();
    }
  }, [user, loadReports]);

  const formatReportDate = (dateString) => {
    if (!dateString) return "Sin fecha";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "Sin fecha";
    return date.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando informes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />

      <main className="flex-1 container mx-auto px-4 pt-20 pb-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate("/dashboard")}
              variant="ghost"
              size="icon"
              className="flex-shrink-0"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Informes de propiedades</h1>
              <p className="text-muted-foreground">
                {reports.length === 0
                  ? "Aún no has generado ningún informe."
                  : `${reports.length} informe${
                      reports.length !== 1 ? "s" : ""
                    } disponible${
                      reports.length !== 1 ? "s" : ""
                    } para tus viviendas analizadas.`}
              </p>
            </div>
          </div>
        </div>

        {reports.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              No tienes informes generados
            </h2>
            <p className="text-muted-foreground mb-6">
              Los informes PDF que generes desde el analizador aparecerán aquí.
            </p>
            <Button onClick={() => navigate("/analyzer")} variant="hero">
              Generar mi primer informe
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reports.map((report) => {
              const pdfUrl = getReportPdfUrl(report.pdf_path, report.pdf_url);
              const reportTitle =
                report.property_title ||
                report.property_url ||
                "Informe de propiedad";
              const reportDate = formatReportDate(report.created_at);
              const propertyUrl = report.property_url;

              return (
                <div
                  key={report.id}
                  className="bg-card rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col"
                >
                  {/* Cabecera visual */}
                  <div className="relative h-40 bg-gradient-to-br from-primary/10 via-primary/5 to-accent/10 flex items-center justify-center">
                    <div className="flex flex-col items-center justify-center text-primary">
                      <FileText className="w-10 h-10 mb-2" />
                      <p className="text-xs font-medium px-2 py-1 rounded-full bg-background/80 text-primary">
                        {reportDate}
                      </p>
                    </div>
                  </div>

                  {/* Contenido */}
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-semibold text-lg mb-2 line-clamp-2">
                      {reportTitle}
                    </h3>

                    <div className="flex items-center gap-1 text-muted-foreground mb-4 text-xs">
                      {propertyUrl ? (
                        <>
                          <ExternalLink className="w-3.5 h-3.5" />
                          <span className="truncate">{propertyUrl}</span>
                        </>
                      ) : (
                        <span>Enlace de propiedad no disponible</span>
                      )}
                    </div>

                    <div className="mt-auto flex flex-col gap-2">
                      <Button
                        onClick={() => {
                          if (pdfUrl) {
                            window.open(
                              pdfUrl,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }
                        }}
                        variant="hero"
                        className="w-full"
                        disabled={!pdfUrl}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Ver informe PDF
                      </Button>
                      {propertyUrl && (
                        <Button
                          onClick={() =>
                            window.open(
                              propertyUrl,
                              "_blank",
                              "noopener,noreferrer"
                            )
                          }
                          variant="outline"
                          className="w-full"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Ver vivienda en Idealista
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default Reports;


